import { Ticket, TicketComment, TicketCommentScope } from './ticketWorkflow'

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isString(value: unknown): value is string {
  return typeof value === 'string'
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean'
}

/**
 * Exhaustive by construction — `Record<Union, true>` stops compiling if a scope
 * is added or renamed, so this cannot fall behind the type it guards.
 */
const COMMENT_SCOPES: Record<TicketCommentScope, true> = {
  PARTICIPANT_PUBLIC: true,
  ADOTEL_INTERNAL: true,
  REPORTER_AND_ASSIGNEE_ONLY: true
}

function isCommentScope(value: unknown): value is TicketCommentScope {
  return isString(value) && Object.hasOwnProperty.call(COMMENT_SCOPES, value)
}

function isEntityRef(value: unknown): boolean {
  return isRecord(value) && isString(value.type) && isString(value.code)
}

function isTicket(value: unknown): value is Ticket {
  if (!isRecord(value)) return false
  return (
    [
      value.id,
      value.ticketNumber,
      value.containerResolutionStatus,
      value.intakeChannel,
      value.state,
      value.submissionChannel,
      value.productScope,
      value.title,
      value.createdAt,
      value.changedAt,
      // Required on `Ticket`, and the narrowing is compile-time only — so a
      // response omitting `assigneeUserId` used to flow downstream as
      // `undefined`, where `assignmentKind`'s `=== ''` test sends it into the
      // 'user' branch and the assignee column renders the string "undefined".
      value.assigneeUserId
    ].every(isString) &&
    isBoolean(value.assigneeIsAdotelAdmin) &&
    isBoolean(value.hasDiagnosticContext) &&
    typeof value.version === 'number' &&
    Array.isArray(value.containerRefs) &&
    value.containerRefs.every(isEntityRef) &&
    Array.isArray(value.affectedEntityRefs) &&
    value.affectedEntityRefs.every(isEntityRef) &&
    Array.isArray(value.watcherUserIds) &&
    value.watcherUserIds.every(isString)
  )
}

function isComment(value: unknown): value is TicketComment {
  if (!isRecord(value)) return false
  return (
    [
      value.id,
      value.ticketId,
      value.authorUserId,
      value.body,
      value.createdAt
    ].every(isString) &&
    // Checked against the union, not merely `isString` — scope drives what the
    // UI labels a comment as, so an unknown value must be rejected rather than
    // rendered as a raw protocol string.
    isCommentScope(value.scope) &&
    isBoolean(value.redacted)
  )
}

function isPage(
  value: unknown,
  itemField: 'tickets' | 'comments',
  item: (entry: unknown) => boolean
): boolean {
  if (!isRecord(value)) return false
  const items = value[itemField]
  return (
    Array.isArray(items) &&
    (value.nextPageToken === null ||
      value.nextPageToken === undefined ||
      isString(value.nextPageToken)) &&
    items.every(item)
  )
}

export function validateTicketsResponse(data: unknown): string | undefined {
  if (!isRecord(data) || !isPage(data.tickets, 'tickets', isTicket)) {
    return 'Received an invalid ticket response.'
  }
}

export function validateAgentTicketsResponse(
  data: unknown
): string | undefined {
  if (
    !isRecord(data) ||
    !isPage(data.supportAgentTickets, 'tickets', isTicket)
  ) {
    return 'Received an invalid support queue response.'
  }
}

export function validateCommentsResponse(data: unknown): string | undefined {
  if (!isRecord(data) || !isPage(data.ticketComments, 'comments', isComment)) {
    return 'Received an invalid conversation response.'
  }
}
